import first from "lodash/first";
import { Fragment, type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { getAbsoluteUrl, notEmpty } from "shared/lib/utils";

import { ContentItemMultimediaV2 } from "scmp-app/components/content/content-item-render/variants/multimedia-v2";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { multimediaHomeV2Query$key } from "scmp-app/queries/__generated__/multimediaHomeV2Query.graphql";

import {
  Container,
  Divider,
  Item1,
  Item2,
  Item3,
  Item4,
  Item5,
  ItemContainer,
  StyledItemPodcast,
  Title,
} from "./styles";

type Props = {
  className?: string;
  reference: multimediaHomeV2Query$key;
};

export const HomeV2Multimedia: FunctionComponent<Props> = ({ className, reference }) => {
  const data = useFragment(
    graphql`
      fragment multimediaHomeV2Query on Query
      @argumentDefinitions(multimediaQueueName: { type: "String!" }) {
        multimediaV2: queue(filter: { name: $multimediaQueueName }) {
          items(first: 5) {
            edges {
              node {
                __typename
                ... on Content {
                  entityId
                  ... on Article {
                    types {
                      value {
                        name
                      }
                    }
                  }
                  ...multimediaPodcastV2ContentItemContent
                  ...multimediaV2ContentItemContent
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );
  const multimediaItems = data.multimediaV2?.items?.edges ?? [];

  const handleClick = (entityId?: string, urlAlias?: string) => {
    if (!notEmpty(entityId)) return;
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        destination_url: getAbsoluteUrl(urlAlias),
        page_type: "homepage",
        widget_name: "multimedia",
      },
      subcategory: "widget",
    });
  };

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: () => ({
        action: "imp",
        category: "recirculation",
        customized_parameters: {
          page_type: "homepage",
          widget_name: "multimedia",
        },
        subcategory: "widget",
      }),
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  const itemComponents = [Item1, Item2, Item3, Item4, Item5];

  const renderItem = (item: (typeof multimediaItems)[number], index: number) => {
    const mainArticleType = first(item?.node?.types);
    const articleTypeName = first(mainArticleType?.value)?.name;
    const isPodcast = item?.node?.__typename === "Podcast" || articleTypeName === "Podcast";
    const Component = itemComponents[index];

    return (
      <Component>
        {isPodcast ? (
          <StyledItemPodcast
            key={item?.node.entityId}
            onClick={handleClick}
            reference={item?.node}
          />
        ) : (
          <ContentItemMultimediaV2 onClick={handleClick} reference={item?.node} />
        )}
      </Component>
    );
  };

  if (multimediaItems.length === 0) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.Multimedia,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <Title as="/video" pathname="/video">
          MULTIMEDIA
        </Title>
        <ItemContainer>
          {multimediaItems.map((item, index) => (
            <Fragment key={item?.node.entityId}>
              {renderItem(item, index)}
              {index === 2 && <Divider />}
            </Fragment>
          ))}
        </ItemContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2Multimedia.displayName = "HomeV2Multimedia";
